/**
 * PanelCard — collapsible container card for the Property Panel design system.
 *
 * The primary unit of hierarchy: each logical group of fields lives in its
 * own bordered card with a consistent header (icon + title + optional badge
 * + chevron). Containers do the visual work, so titles can be sentence-case
 * and low-weight.
 *
 * Variants:
 *   - "standard" (default) — white surface, hairline border, collapsible
 *   - "loanInfo"           — the Loan Info redesign tab's card chrome (softer
 *                            radius, visible border + shadow, cyan icon, dark
 *                            600-weight title). Use where sidebar cards sit
 *                            alongside or mirror that tab.
 *   - "alert"              — danger surface/border, NOT collapsible (urgency
 *                            shouldn't be dismissable)
 *   - "subtle"             — borderless, muted surface, for de-emphasized
 *                            groupings
 *
 * Expand/collapse all: wrap a group of cards in `PanelCardCollapseProvider` and
 * bump its `token` to drive every card inside to `open`. Done through context
 * rather than a controlled prop so hosts don't have to thread state through
 * every intermediate section component.
 *
 * Not to be confused with the older `SidebarSection` — PanelCard is the
 * design-system card and is meant to be reused beyond the sidebar.
 */
import { ExpandLess as ChevronIcon } from "@mui/icons-material";
import { Box, Collapse } from "@mui/material";
import { createContext, memo, useContext, useEffect, useState } from "react";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";
import { PANEL_TOKENS as T } from "../../primitives/panel/panelTokens";
// import { PANEL_TOKENS as T } from "../primitives/panel/panelTokens";

/* Loan Info redesign card chrome — kept together so the two stay in step. */
const LOAN_INFO_CHROME = {
  border: "1px solid #E4E7EC",
  radius: "14px",
  shadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
  headerRule: "1px solid #eef1f4",
  headerHover: "#fafbfc",
  iconColor: "#0891b2",
  titleColor: "#0f172a",
};

/**
 * Expand/collapse-all signal. `token` changing is what forces cards to sync —
 * the value itself is arbitrary, only the change matters, so re-collapsing one
 * card by hand doesn't get undone until the next Expand/Collapse All click.
 */
const PanelCardCollapseContext = createContext(null);

export const PanelCardCollapseProvider = PanelCardCollapseContext.Provider;

const PanelCard = memo(
  ({
    icon,
    title,
    badge,
    defaultOpen = true,
    variant = "standard", // "standard" | "alert" | "subtle"
    disableCollapse = false,
    headerEndContent,
    subTitle,
    children,
    sx,
    py = "4px",
    bodySx,
  }) => {
    const [open, setOpen] = useState(defaultOpen);

    const isAlert = variant === "alert";
    const isSubtle = variant === "subtle";
    const isLoanInfo = variant === "loanInfo";
    // Alert cards are never collapsible — see rule #6 in the design doc.
    const collapsible = !disableCollapse && !isAlert;
    const expanded = collapsible ? open : true;

    // Expand/collapse all — react to the token changing, not to `signal.open`,
    // so a card the user collapsed by hand stays collapsed until the next click.
    const signal = useContext(PanelCardCollapseContext);
    const signalToken = signal?.token;
    const signalOpen = signal?.open;
    useEffect(() => {
      if (signalToken === undefined || signalToken === null) return;
      setOpen(!!signalOpen);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signalToken]);

    const surface = isAlert
      ? T.color.dangerBg
      : isSubtle
        ? T.color.bgSubtle
        : T.color.bgCard;
    const borderColor = isAlert ? T.color.dangerBorder : T.color.borderDefault;
    const titleColor = isAlert
      ? T.color.dangerTextStrong
      : isLoanInfo
        ? LOAN_INFO_CHROME.titleColor
        : T.color.textPrimary;
    const iconColor = isAlert
      ? T.color.dangerText
      : isLoanInfo
        ? LOAN_INFO_CHROME.iconColor
        : T.color.textSecondary;

    return (
      <Box
        sx={{
          background: surface,
          border: isSubtle
            ? "none"
            : isLoanInfo
              ? LOAN_INFO_CHROME.border
              : `0.5px solid ${borderColor}`,
          borderRadius: isLoanInfo ? LOAN_INFO_CHROME.radius : T.radius.lg,
          ...(isLoanInfo ? { boxShadow: LOAN_INFO_CHROME.shadow } : {}),
          mb: 1.5,
          overflow: "hidden",
          ...sx,
        }}
      >
        <Box
          onClick={collapsible ? () => setOpen((o) => !o) : undefined}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: isLoanInfo ? "16px" : T.space.cardPaddingX,
            pt: isLoanInfo ? "16px" : undefined,
            pb: isLoanInfo ? "12px" : undefined,
            py: isLoanInfo ? undefined : T.space.cardPaddingY,
            cursor: collapsible ? "pointer" : "default",
            userSelect: "none",
            borderBottom: isLoanInfo
              ? expanded
                ? LOAN_INFO_CHROME.headerRule
                : "1px solid transparent"
              : expanded
                ? `0.5px solid ${borderColor}`
                : "0.5px solid transparent",
            transition: "background-color 0.1s",
            "&:hover": collapsible
              ? {
                  backgroundColor: isLoanInfo
                    ? LOAN_INFO_CHROME.headerHover
                    : T.color.bgSubtle,
                }
              : undefined,
          }}
        >
          {subTitle ? (
            <Flx column g={0.5} sx={{ minWidth: 0 }}>
              <Flx ac g={1} sx={{ minWidth: 0 }}>
                {icon && (
                  <Box
                    sx={{
                      display: "flex",
                      flexShrink: 0,
                      color: iconColor,
                      "& svg": { fontSize: isLoanInfo ? 18 : 16 },
                    }}
                  >
                    {icon}
                  </Box>
                )}
                <Txt
                  sx={{
                    fontSize: T.text.md,
                    fontWeight: isLoanInfo ? 600 : T.weight.medium,
                    color: titleColor,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {title}
                </Txt>
                {badge != null && badge !== "" && (
                  <Txt
                    sx={{
                      flexShrink: 0,
                      fontSize: T.text.xs,
                      color: T.color.textTertiary,
                      px: "6px",
                      py: "2px",
                      borderRadius: T.radius.sm,
                      background: isAlert
                        ? "rgba(255,255,255,0.55)"
                        : T.color.bgSubtle,
                    }}
                  >
                    {badge}
                  </Txt>
                )}
              </Flx>
              <Txt secondary>{subTitle}</Txt>
            </Flx>
          ) : (
            <Flx ac g={1} sx={{ minWidth: 0 }}>
              {icon && (
                <Box
                  sx={{
                    display: "flex",
                    flexShrink: 0,
                    color: iconColor,
                    "& svg": { fontSize: isLoanInfo ? 18 : 16 },
                  }}
                >
                  {icon}
                </Box>
              )}
              <Txt
                sx={{
                  fontSize: T.text.md,
                  fontWeight: isLoanInfo ? 600 : T.weight.medium,
                  color: titleColor,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title}
              </Txt>
              {badge != null && badge !== "" && (
                <Txt
                  sx={{
                    flexShrink: 0,
                    fontSize: T.text.xs,
                    color: T.color.textTertiary,
                    px: "6px",
                    py: "2px",
                    borderRadius: T.radius.sm,
                    background: isAlert
                      ? "rgba(255,255,255,0.55)"
                      : T.color.bgSubtle,
                  }}
                >
                  {badge}
                </Txt>
              )}
            </Flx>
          )}
          <Flx ac g={0.5} sx={{ flexShrink: 0 }}>
            {headerEndContent && (
              // Stop clicks on header-end content (buttons, inputs, etc.)
              // from bubbling up to the header's collapse toggle — interacting
              // with an action there shouldn't expand/collapse the card.
              <Box
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                sx={{ display: "inline-flex", alignItems: "center" }}
              >
                {headerEndContent}
              </Box>
            )}
            {collapsible && (
              <ChevronIcon
                sx={{
                  fontSize: 14,
                  color: T.color.textSecondary,
                  transition: "transform 150ms ease",
                  transform: open ? "rotate(0deg)" : "rotate(180deg)",
                }}
              />
            )}
          </Flx>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ px: T.space.cardPaddingX, py, ...bodySx }}>{children}</Box>
        </Collapse>
      </Box>
    );
  },
);

PanelCard.displayName = "PanelCard";

export default PanelCard;
